/**
 * 글로릴스(flowing-ai-avatar) 연동 — 인스타 국가별 계정 프로필 조회 + 블록 원문 동기화.
 *
 * 서버 명세: documents/gloreels-sodu-integration-master.md §4·§5
 * 인증: 소듀 access_token Bearer — 글로릴스가 플로잉 /auth/web/user 로 검증한다.
 * 인스타 액세스 토큰은 절대 오가지 않는다(DB 스냅샷만).
 *
 */
import { GLOREELS_ORIGIN, GLOREELS_SSO_ENABLED } from '@/constant/sso';
import sessionStore from '@/store/modules/sessionStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';
import { TRANSLATABLE_FIELDS as I18N_FIELDS, ARRAY_TRANSLATABLE_FIELDS } from '@/utils/profileLinkI18n';

/** 프로필 소개글을 번역 파이프라인에 태우기 위한 합성 블록 ID (블록 아님) */
export const PROFILE_INTRO_BLOCK_ID = '__profile_intro__';

/** 글로릴스에 연동된 인스타 계정 하나 (언어=국가 단위) */
export interface GlorillsProfile {
    /** 언어 코드 — 글로릴스 SocialSlot.lang (ko/en/ja/zh…) */
    lang: string;
    /** 인스타 핸들 (@ 제외) */
    username: string;
    /** 인스타 표시 이름 */
    name: string;
    /** 프로필 이미지 — 글로릴스가 미러링한 영구 URL */
    avatarUrl: string | null;
    /** 인스타 소개글(bio). 글로릴스에서 못 받아오면 null → 고객이 직접 입력 */
    bio: string | null;
}


/**
 * 연동된 인스타 계정 목록 조회 — 글로릴스 파트너 API.
 * 실패·빈 결과여도 throw 하지 않는다(편집 화면은 국가 버튼 없이 정상 동작해야 한다).
 *
 * 반환 구분:
 *  - `GlorillsProfile[]` : 인증돼 조회한 확정 결과(빈 배열 = 연동 계정 없음 → 칩 미표시)
 *  - `null`              : "아직 준비 안 됨"(킬스위치 OFF / 토큰 미도착 / 네트워크 실패).
 *                          호출자가 이걸 확정 결과로 캐시하면 안 된다 — 토큰이 늦게 rehydrate
 *                          되는 SSO 리다이렉트 복귀 직후 빈 결과가 굳어버려 새로고침 전엔
 *                          국가 칩이 안 뜨는 레이스를 막기 위함이다.
 */
export async function fetchGlorillsProfiles(_userId: string): Promise<GlorillsProfile[] | null> {
    if (!GLOREELS_SSO_ENABLED) return [];
    const { accessToken } = sessionStore.getState();
    // 토큰이 아직 없으면 "미확정" — 빈 배열로 캐시되지 않게 null 을 돌린다.
    if (!accessToken) return null;
    try {
        const res = await fetch(`${GLOREELS_ORIGIN}/api/partner/social-profiles`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
            const data = await res.json().catch(() => null);
            if (Array.isArray(data?.profiles)) {
                // 저장된 "소개 SNS 사용" 토글을 편집기 상태에 되읽는다(백엔드 미저장분을 글로릴스에서 복원).
                if (typeof data?.use_sns_intro === 'boolean') {
                    spaceUserV2Store.getState().patchSpaceUserV2Info({ use_sns_intro: data.use_sns_intro });
                }
                return data.profiles as GlorillsProfile[];
            }
            return null; // 200 이지만 형식 미상 — 확정 취급하지 않는다
        }
        // 401 등 인증 실패도 미확정(토큰 갱신 후 재시도 여지) — null
        return null;
    } catch (e) {
        console.warn('글로릴스 프로필 조회 실패:', e);
        return null;
    }
}

// ─── 블록 원문 동기화 (→ 자동 번역 트리거) ─────────────────────

/**
 * 원문이 이미 영어인 텍스트는 번역하지 않고 그대로 둔다(사용자 확정).
 * push 에서 제외하면 번역본이 생기지 않고, 렌더는 번역이 없으면 원본을
 * 그대로 보여주므로 모든 언어에서 영어 원문이 유지된다.
 */
function isEnglishText(v: string): boolean {
    return !/[가-힣]/.test(v) && /[A-Za-z]/.test(v);
}

let lastPushHash = '';

/**
 * 블록 원문을 글로릴스로 push — 저장 성공 직후 fire-and-forget 으로 호출한다.
 *
 * 번역 대상 필드는 편집 폼·미리보기와 같은 정본(utils/profileLinkI18n 의
 * TRANSLATABLE_FIELDS)에서 도출한다. link_url 은 "언어별 다른 링크" 용도라
 * 자동 번역 대상이 아니므로 제외. 캘린더(schedule) 항목 제목은
 * `schedule_content#<schedule_id>.title` 평면 키로 함께 보낸다.
 *
 * 글로릴스는 이 원문과 연동 언어 목록으로 (블록 × 언어) 번역을 재조정한다.
 * 같은 내용의 중복 push 는 해시로 걸러 페이지 로드마다 재전송하지 않는다.
 */
export function pushBlocksToGlorills(blocks: any[]): void {
    if (!GLOREELS_SSO_ENABLED) return;
    const { accessToken } = sessionStore.getState();
    if (!accessToken || !Array.isArray(blocks)) return;

    const payload = blocks
        .map((b: any) => {
            const type = b?.block_type as string;
            const content = b?.block_content ?? {};
            const fields: Record<string, string> = {};

            // 상위 텍스트 필드 (title·sub_title·group_title 등)
            for (const f of I18N_FIELDS[type] ?? []) {
                if (f.key === 'link_url') continue; // 텍스트가 아님
                const v = content[f.key];
                if (typeof v === 'string' && v.trim() && !isEnglishText(v)) fields[f.key] = v;
            }

            // 중첩 배열 항목 텍스트 — 평면 키 `<field>#<id>.<textKey>` (schedule·calendar·badge·
            // partners·collection 을 한 표로 통합). 규약·매칭키는 profileLinkI18n 과 공유한다.
            for (const spec of ARRAY_TRANSLATABLE_FIELDS[type] ?? []) {
                const arr = content[spec.field];
                if (!Array.isArray(arr)) continue;
                for (const item of arr) {
                    const id = item?.[spec.idKey];
                    if (!id) continue;
                    for (const tkey of spec.textKeys) {
                        const v = item?.[tkey];
                        if (typeof v === 'string' && v.trim() && !isEnglishText(v)) {
                            fields[`${spec.field}#${id}.${tkey}`] = v;
                        }
                    }
                }
            }

            // [비용 절감] 언어별 숨김 목록 — 예약키 `_hidden_langs`(JSON 문자열, `_` 접두라
            // 해시·번역 대상에서 자동 제외). 글로릴스 reconcile 이 lang∈hidden 인 (블록,언어)
            // 번역 생성을 스킵한다. 숨김 해제되면 레코드가 없어 자동 재번역. 빈 배열이면 생략.
            const hidden = Array.isArray(content.hidden_langs)
                ? content.hidden_langs.filter((l: unknown) => typeof l === 'string' && l)
                : [];
            if (hidden.length > 0) fields._hidden_langs = JSON.stringify(hidden);

            return { blockId: b?.block_id, type, fields };
        })
        .filter((b) => b.blockId && Object.keys(b.fields).length > 0);

    // 프로필 소개글도 같은 번역 파이프라인에 태운다(프로필 필드라 합성 블록으로).
    // 소개 SNS 사용 토글(use_sns_intro)은 예약 필드 `_use_sns_intro`("1"/"0")로 함께 실어
    // 보낸다 — 글로릴스가 번역하지 않고 보관하고, 공개/편집이 social-profiles 로 되읽는다.
    // 소개글이 비어도 토글값 전달을 위해 항상 이 블록을 넣는다.
    // [비용 절감] SNS 사용 ON 이면 소개글은 인스타 bio(이미 각국어)를 쓰므로 번역이 버려진다
    //  → ON 일 때는 description 을 아예 push 하지 않아 (1블록 × 언어수) 번역을 아낀다.
    //  OFF 로 전환하면 그때 push 에 description 이 실려 번역된다(토글 변경이 push 를 트리거).
    const { description, use_sns_intro } = spaceUserV2Store.getState();
    const introFields: Record<string, string> = { _use_sns_intro: use_sns_intro ? '1' : '0' };
    if (!use_sns_intro && typeof description === 'string' && description.trim() && !isEnglishText(description)) {
        introFields.description = description;
    }
    payload.push({ blockId: PROFILE_INTRO_BLOCK_ID, type: 'text', fields: introFields });

    const hash = JSON.stringify(payload);
    if (hash === lastPushHash) return;
    lastPushHash = hash;

    void fetch(`${GLOREELS_ORIGIN}/api/partner/blocks-sync`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ blocks: payload }),
        keepalive: true,
    }).catch(() => {
        lastPushHash = ''; // 실패 시 다음 기회에 다시
    });
}

/**
 * 글로릴스 자동번역을 블록 i18n 형식으로 pull — 공개 엔드포인트(CORS *, 무인증).
 * 응답 형식이 block_content.i18n[lang] 과 동일하다(같은 평면키 규약: schedule_content#<id>.title).
 * 반환: { blockId: { field: 번역문 } }. 실패·비활성은 빈 객체.
 */
export async function fetchGlorillsTranslations(
    soduUserId: string,
    lang: string,
): Promise<Record<string, Record<string, string>>> {
    if (!GLOREELS_SSO_ENABLED || !soduUserId) return {};
    try {
        const res = await fetch(
            `${GLOREELS_ORIGIN}/api/partner/translations?soduUserId=${encodeURIComponent(soduUserId)}&lang=${encodeURIComponent(lang)}`,
        );
        if (!res.ok) return {};
        const data = await res.json().catch(() => null);
        return data?.translations && typeof data.translations === 'object' ? data.translations : {};
    } catch {
        return {};
    }
}

/**
 * 글로릴스 번역을 각 블록의 block_content.i18n 으로 병합한다 (B안 — 단일 소스로 통일).
 *
 * 편집기·공개페이지 둘 다 block_content.i18n 을 읽으므로, 글로릴스 서버의 자동번역을
 * 여기로 끌어와 원래 블록 데이터에 심는다. 기존 i18n 값(사용자 수정분)은 덮지 않고,
 * 비어 있는 필드만 자동번역으로 채운다.
 *
 * @returns 변경된 블록만 담은 목록(저장 대상). 변경 없으면 빈 배열.
 */
export async function syncGlorillsTranslationsToBlocks(
    soduUserId: string,
    blockList: any[],
    langs: string[],
): Promise<{ merged: any[]; changed: any[]; descriptionI18n: Record<string, string> }> {
    const targetLangs = langs.filter((l) => l && l !== 'ko');
    if (!GLOREELS_SSO_ENABLED || !soduUserId || targetLangs.length === 0 || !Array.isArray(blockList)) {
        return { merged: blockList, changed: [], descriptionI18n: {} };
    }
    // lang -> { blockId -> {field: 번역} }
    const byLang: Record<string, Record<string, Record<string, string>>> = {};
    await Promise.all(
        targetLangs.map(async (lang) => {
            byLang[lang] = await fetchGlorillsTranslations(soduUserId, lang);
        }),
    );

    // 프로필 소개글 번역(합성 블록) 추출 — description_i18n 으로 반환
    const descriptionI18n: Record<string, string> = {};
    for (const lang of targetLangs) {
        const t = byLang[lang]?.[PROFILE_INTRO_BLOCK_ID]?.description;
        if (typeof t === 'string' && t.trim()) descriptionI18n[lang] = t;
    }

    const changed: any[] = [];
    const merged = blockList.map((block) => {
        const blockId = block?.block_id;
        if (!blockId) return block;
        const curI18n: Record<string, Record<string, string>> = block?.block_content?.i18n ?? {};
        let blockChanged = false;
        const nextI18n: Record<string, Record<string, string>> = { ...curI18n };

        for (const lang of targetLangs) {
            const fields = byLang[lang]?.[blockId];
            if (!fields || Object.keys(fields).length === 0) continue;
            const existing = curI18n[lang] ?? {};
            const nextForLang = { ...existing };
            for (const [k, v] of Object.entries(fields)) {
                // 비어있는 필드만 자동번역으로 채운다(사용자 수정분 보존)
                if (typeof v === 'string' && v.trim() && !existing[k]) {
                    nextForLang[k] = v;
                    blockChanged = true;
                }
            }
            if (blockChanged) nextI18n[lang] = nextForLang;
        }

        if (!blockChanged) return block;
        const next = { ...block, block_content: { ...block.block_content, i18n: nextI18n } };
        changed.push(next);
        return next;
    });

    return { merged, changed, descriptionI18n };
}

// ─── 번역 진행률 (가져오기 직후 폴링) ─────────────────────

/** 글로릴스 `GET /api/partner/translation-progress` 응답 */
export interface GloreelsTranslationProgress {
    linked: boolean;
    /** 번역 대상 언어(원본 ko 제외) */
    langs: string[];
    /** 글로릴스에 도착한 블록 원문 수. 0 이면 아직 push 가 안 된 것 */
    blocks: number;
    /** (블록 × 언어) 번역 대상 조합 수 */
    total: number;
    done: number;
    pending: number;
    /** 0~100 */
    percent: number;
    /** 예상 잔여 초 */
    estimatedSeconds: number;
    complete: boolean;
}

/**
 * 번역 진행률 조회. 가져오기(연동) 직후 편집기 블록이 2~3초 간격으로 불러 진행 막대를 그린다.
 * 서버가 no-store 로 내리고, 여기서도 캐시를 쓰지 않는다. 실패는 null(호출자가 다음 폴링에 재시도).
 */
export async function fetchGloreelsTranslationProgress(
    soduUserId: string,
): Promise<GloreelsTranslationProgress | null> {
    if (!GLOREELS_SSO_ENABLED || !soduUserId) return null;
    try {
        const res = await fetch(
            `${GLOREELS_ORIGIN}/api/partner/translation-progress?soduUserId=${encodeURIComponent(soduUserId)}`,
            { cache: 'no-store' },
        );
        if (!res.ok) return null;
        const d = await res.json().catch(() => null);
        if (!d || typeof d.percent !== 'number') return null;
        return {
            linked: !!d.linked,
            langs: Array.isArray(d.langs) ? d.langs.filter((l: unknown) => typeof l === 'string') : [],
            blocks: Number(d.blocks) || 0,
            total: Number(d.total) || 0,
            done: Number(d.done) || 0,
            pending: Number(d.pending) || 0,
            percent: Math.max(0, Math.min(100, Math.round(d.percent))),
            estimatedSeconds: Number(d.estimatedSeconds) || 0,
            complete: !!d.complete,
        };
    } catch {
        return null;
    }
}
